<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Add Exact Match Rule
          </div>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-loader
        v-if="loading"
        style="margin: 5rem"
      />
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
        margin="0"
        style="padding: 5rem"
      />
      <div v-else>
        <table>
          <tbody>
            <tr>
              <th colspan="2" class="category">
                Rule Information
              </th>
            </tr>
            <tr>
              <td
                class="has-text-grey"
              >
                Rule Name
              </td>
              <td>
                <div class="control align-right">
                  <input
                    class="input rule-name"
                    style="max-width: 215px"
                    type="text"
                    v-model="ruleName"
                    :aria-label="ruleName || 'Rule Name'"
                    :class="{'is-danger' : hasErrName}"
                  >
                </div>
                <span
                  v-if="hasErrName"
                  class="help is-danger"
                >
                  {{ msgErrName }}
                </span>
              </td>
            </tr>
            <tr>
              <td
                class="has-text-grey"
                style="vertical-align: middle"
              >
                Status
              </td>
              <td>
                <div class="control align-right">
                  <div class="buttons has-addons">
                    <button
                      class="button button--form"
                      v-for="(option, index) in enabledDisabled"
                      :class="{ 'is-active': status === option.code }"
                      :key="index"
                      @click="status = option.code"
                    >
                      {{ option.description }}
                    </button>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-grey">
                Person Type
              </td>
              <td class="no-border">
                <div class="control align-right">
                  <es-select
                    style="max-width: 215px"
                    keyValue="id"
                    valueTpl="${name}"
                    :preSelected="preSelectedPersonType"
                    :setValues="sortedAvailablePersonTypes"
                    @update:values="personType = $event[0].code"
                  />
                </div>
              </td>
            </tr>
            <tr>
              <th
                class="category"
                colspan="2"
                style="padding-bottom: 0px"
              >
                Source(s)
              </th>
            </tr>
            <tr>
              <th style="border: 0; padding: 0px 10.5px" colspan="2">
                <div class="control full-width">
                  <es-select
                    keyValue="code"
                    valueTpl="${description}"
                    :clearOut="true"
                    :errorMsg="msgErrSource"
                    :hasError="hasErrSource"
                    :multi="true"
                    :setValues="activeSources"
                    @update:values="source = $event.map(s => s.code)"
                  />
                </div>
              </th>
            </tr>
            <tr>
              <th class="category" style="padding-bottom: 0px" colspan="2">
                Field(s)
              </th>
            </tr>
            <tr>
              <th style="border: 0; padding: 0px 10.5px" colspan="2">
                <div class="control">
                  <es-select
                    keyValue="code"
                    valueTpl="${description}"
                    :clearOut="true"
                    :errorMsg="msgErrFields"
                    :hasError="hasErrFields"
                    :multi="true"
                    :setValues="fieldOptions"
                    @update:values="fields = $event.map(f => f.code)"
                  />
                </div>
              </th>
            </tr>
          </tbody>
        </table>
        <div>
          <div class="is-flex flex-justify--end flex-align--center"
            style="padding-bottom: 2rem"
          >
            <button
              class="button button-link has-underline"
              @click.prevent="cancelPanel()"
              style="margin-right: 1rem"
            >
              Cancel
            </button>
            <button
              class="button is-primary"
              @click.prevent="addRule()"
            >
              Add Exact Match Rule
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  props: {
    sortedAvailablePersonTypes: {
      type: Array,
    },
  },
  watch: {
    personType() {
      this.fieldLabels(this.personType);
    },
  },
  created() {
    this.enabledDisabled = [
      {
        code: true,
        description: 'Enabled',
      },
      {
        code: false,
        description: 'Disabled',
      },
    ];
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
    this.personType = this.sortedAvailablePersonTypes
      .filter(p => p.code === this.userPersonType)[0].code;
    this.preSelectedPersonType = this.sortedAvailablePersonTypes
      .filter(p => p.code === this.userPersonType);
    this.fieldLabels(this.userPersonType);
  },
  mounted() {
    this.onLoad();
  },
  data() {
    return {
      fields: [],
      fieldOptions: [],
      hasErrFields: false,
      hasErrName: false,
      hasErrSource: false,
      loading: false,
      loadingError: false,
      msgErrFields: null,
      msgErrName: null,
      msgErrSource: null,
      personType: [],
      ruleName: null,
      source: [],
      status: true,
      userPersonTypesCodes: null,
    };
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('lookups', [
      'activeSources',
      'defaultPersonType',
    ]),
    payLoad() {
      const arr = {};
      arr.personType = this.personType;
      arr.ruleName = this.ruleName;
      arr.sourcesJson = JSON.stringify({ sources: this.source });
      arr.enable = this.status === true ? 1 : 0;
      arr.matchFieldsJson = JSON.stringify({ fields: this.fields });
      return arr;
    },
    userPersonType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadSources',
    ]),
    async addRule() {
      this.checkRequiredFields();
      if (this.hasErrName || this.hasErrSource || this.hasErrFields) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      try {
        this.loading = true;
        this.loadingError = false;
        await axios({
          method: 'post',
          url: '/rule-manager/person-id/exact-match-rules',
          data: this.payLoad,
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
        this.$emit('toggleExactMatchRules');
        this.$emit('allowEditOtherPanels');
        this.showNotification('success', ['The Exact Match Rule has been added.']);
      }
    },
    cancelPanel() {
      this.hideNotification();
      this.$emit('allowEditOtherPanels');
      this.$emit('toggleExactMatchRules');
    },
    checkRequiredFields() {
      const checkField = (field, errorProp, errorMsg, msg) => {
        if (!field) {
          this[errorProp] = true;
          this[msg] = errorMsg;
        } else {
          this[errorProp] = false;
          this[msg] = null;
        }
      };
      checkField(this.ruleName, 'hasErrName', 'Rule Name is required.', 'msgErrName');
      checkField(this.source.length, 'hasErrSource', 'Source is required.', 'msgErrSource');
      checkField(this.fields.length, 'hasErrFields', 'Field is required.', 'msgErrFields');
    },
    compare(a, b) {
      if (a.description.toUpperCase() < b.description.toUpperCase()) {
        return -1;
      }
      if (a.description.toUpperCase() > b.description.toUpperCase()) {
        return 1;
      }
      return 0;
    },
    fieldLabels(pType) {
      const codes = [
        'FIRST_NAME', 'LAST_NAME', 'MIDDLE_NAME', 'SUFFIX', 'PREVIOUS_LAST_NAME',
        'DATE_OF_BIRTH', 'GENDER_CODE', 'RACE_ETHNIC_CODE', 'ETHNICITY_INDICATOR',
        'DISTRICT_CODE', 'SCHOOL_CODE', 'RES_DISTRICT_CODE', 'LOCAL_STUDENT_ID',
        'SOURCE_CODE', 'GRADE_CODE', 'SCHOOL_YEAR', 'SOCIAL_SECURITY_NUMBER',
      ];
      const temp = codes.map(code => ({
        code,
        description: this.getFieldLabel(`PER:${pType}:ASSIGN:${code}`),
      }));
      this.fieldOptions = temp.sort(this.compare);
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadSources(),
        ]);
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    font-size: 12px
    vertical-align: middle
  .category
    padding-top: 1.5rem
    border: 0
  .content
    padding: 0rem 3.5rem
    .control.align-right
      text-align: right
      .field
        display: inline-block
    .control .buttons
      display: inline-flex
</style>
